import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { TagModule } from 'primeng/tag'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import {
  CartItemComponent,
  CartItemModel
} from 'app/_shared/components/cart-item/cart-item.component'
import { OrderSummaryComponent } from 'app/_shared/components/order-summary/order-summary.component'
import { BasePayment, BasePointsCurrency } from 'app/_shared/interfaces/BasePoints'
import { BasePointsPipe } from 'app/_shared/pipes/base-points.pipe'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { OrderService } from 'app/_shared/services/order.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { AppConfigService } from 'app/app-config.service'

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'
type UiPayment = 'paid' | 'pending' | 'failed' | 'store_credit'

@Component({
  selector: 'app-order-confirmation-page',
  templateUrl: './order-confirmation-page.component.html',
  styleUrl: './order-confirmation-page.component.scss',
  imports: [
    UsdPipe,
    PricePipe,
    RouterLink,
    TranslatePipe,
    TagModule,
    ButtonComponent,
    CartItemComponent,
    OrderSummaryComponent,
    BasePointsPipe
  ]
})
export class OrderConfirmationPageComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private orderService = inject(OrderService)
  private toast = inject(ToastService)
  private appConfig = inject(AppConfigService)
  private router = inject(Router)

  order = signal<any | null>(null)
  storefrontId!: number
  uuid = ''
  loading = false
  inAccount = false

  /** If provided, the page will render this order and skip route fetching */
  externalOrder = input<any | null>(null)
  /** Show/hide prices & payment block */
  isKitOrder = input<boolean>(false)
  /** Hide payment block if payments disabled */
  isPaymentDisabled = input<boolean>(false)
  basePayment = input<BasePayment | null>(null)
  baseCurrency = input<BasePointsCurrency>({
    currency_long_name: 'Base points',
    currency_short_name: 'pts',
    currency_prefix: false
  })
  resolvedBasePayment = computed<BasePayment | null>(
    () => this.basePayment() ?? this.order()?.base_payment ?? null
  )
  /** Force showing the “Back to Orders” link when used under my-account */
  forceInAccount = input<boolean | null>(null)
  disablePrices = !!this.appConfig.getAppConfig?.disabled_prices

  private syncExternalOrder = effect(
    () => {
      const provided = this.externalOrder()
      if (provided) this.order.set(provided)
    },
    { allowSignalWrites: true }
  )

  ngOnInit(): void {
    this.storefrontId = this.appConfig.getAppConfig?.id
    this.uuid = this.route.snapshot.paramMap.get('uuid') ?? ''

    // show the back button when rendered under /my-account
    this.inAccount = !!this.route.snapshot.data?.['inAccount']
    if (!this.externalOrder() && this.uuid) {
      this.fetchOrder()
    }
  }

  fetchOrder() {
    this.loading = true
    this.orderService.getOrderConfirmationData(this.storefrontId, this.uuid).subscribe({
      next: res => {
        this.order.set(res.data)
        this.loading = false
      },
      error: err => {
        this.loading = false
        this.toast.error(err.message)
      }
    })
  }

  /* ---------- Items for <app-cart-item> (read-only) ---------- */
  viewItems = computed<CartItemModel[]>(() => {
    const items = this.order()?.items ?? []
    return items.map((it: any) => ({
      product_size_id: it.product_size_id ?? it.product_size?.id ?? null,
      product_variant_id: it.product_variant_id ?? it.product_variant?.id ?? 0,
      slug: it.product?.slug ?? it.product?.id?.toString() ?? '',
      name: it.product?.name ?? 'Item',
      image:
        it.product_variant?.variant_thumbnail?.full_path ??
        it.product?.product_thumbnail?.full_path,
      size: it.product_size?.size ?? it.size ?? '',
      color: it.product_variant?.color ?? it.color ?? null,
      is_gift_card: !!(it.product?.is_gift_card ?? it.is_gift_card),
      price: Number(it.price ?? 0),
      quantity: Number(it.quantity ?? 1),
      input_fields: it.input_fields ?? []
    }))
  })

  itemsSubtotal = computed(() =>
    (this.order()?.items ?? []).reduce(
      (total: number, item: any) =>
        total + Number(item.price ?? 0) * Math.max(0, Number(item.quantity) || 0),
      0
    )
  )

  subtotal = computed(() => {
    const itemSubtotal = this.itemsSubtotal()
    if (itemSubtotal > 0) {
      return itemSubtotal
    }

    return Number(this.order()?.original_subtotal ?? this.order()?.subtotal ?? 0)
  })
  tax = computed(() => Number(this.order()?.original_sales_tax ?? 0))
  shipping = computed(() => Number(this.order()?.original_shipping_price ?? 0))
  flatOrderFeeAmount = computed(() => Number(this.order()?.flat_order_fee_amount ?? 0))
  flatOrderFeeLabel = computed(() => this.order()?.flat_order_fee_label || 'Handling fee')
  handlingFeeAmount = computed(() => {
    const explicitHandlingFee = (this.order()?.items ?? []).reduce((total: number, item: any) => {
      const baseFee = Number(item.handling_fee ?? item.product?.handling_fee ?? 0)
      const additionalItemFee = Number(
        item.handling_fee_additional_item ?? item.product?.handling_fee_additional_item ?? 0
      )
      const quantity = Math.max(0, Number(item.quantity) || 0)

      if (baseFee <= 0 || quantity === 0) {
        return total
      }

      return total + baseFee + Math.max(0, quantity - 1) * additionalItemFee
    }, 0)

    if (explicitHandlingFee > 0) {
      return explicitHandlingFee
    }

    const orderTotalBeforeDiscounts = Number(
      this.order()?.total_without_discounts ?? this.order()?.total ?? 0
    )
    const inferredHandlingFee =
      orderTotalBeforeDiscounts -
      this.subtotal() -
      this.tax() -
      this.shipping() -
      this.flatOrderFeeAmount()

    if (inferredHandlingFee > 0) {
      return inferredHandlingFee
    }

    const originalSubtotal = Number(this.order()?.original_subtotal ?? this.order()?.subtotal ?? 0)
    return Math.max(0, originalSubtotal - this.itemsSubtotal())
  })

  preDiscountTotal = computed(
    () =>
      this.subtotal() +
      this.tax() +
      this.shipping() +
      this.flatOrderFeeAmount() +
      this.handlingFeeAmount()
  )

  discount = computed(() => {
    const total = Number(this.order()?.total ?? this.preDiscountTotal())
    return Math.max(0, this.preDiscountTotal() - total)
  })

  totalDue = computed(() => Math.max(0, this.preDiscountTotal() - this.discount()))

  shippingMethodName = computed(() => this.order()?.shipping_info?.[0]?.data?.name ?? '')
  paymentStatus = computed(() => (this.order()?.payment_status ?? '').toLowerCase())
  private rawPaymentStatus = computed(() => (this.order()?.payment_status ?? '').toLowerCase())

  uiPaymentStatus = computed<UiPayment>(() => {
    const raw = this.rawPaymentStatus()
    if (raw === 'paid' || raw === 'pending' || raw === 'failed') return raw as UiPayment
    return this.isStoreCredit() ? 'store_credit' : 'pending'
  })

  paymentTagSeverity = computed<TagSeverity>(() => {
    const s = this.uiPaymentStatus()
    if (s === 'paid') return 'success'
    if (s === 'pending') return 'warn'
    if (s === 'store_credit') return 'info'
    return 'danger'
  })

  paymentTagIcon = computed(() => {
    const s = this.uiPaymentStatus()
    if (s === 'paid') return 'pi pi-check'
    if (s === 'pending') return 'pi pi-clock'
    if (s === 'store_credit') return 'pi pi-wallet'
    return 'pi pi-times'
  })

  paymentTagValue = computed(() => {
    const s = this.uiPaymentStatus()
    return s === 'store_credit' ? 'STORE CREDIT' : s.toUpperCase()
  })

  // Store Credit when fully covered by gift card
  isBasePayment = computed(() => !!this.resolvedBasePayment())
  isStoreCredit = computed(() => !this.isBasePayment() && Number(this.order()?.total ?? 0) === 0)
  paymentMethodLabel = computed(() => {
    if (this.isBasePayment()) return 'Base points'
    return this.isStoreCredit() ? 'Store Credit' : 'Credit Card'
  })
  last4 = computed(() => this.order()?.card_number_last_four || '----')

  showBack = computed(() => this.forceInAccount() ?? this.inAccount)

  isOrderOlderThan60Minutes = computed(() => {
    const createdAt = this.order()?.created_at
    if (!createdAt) return false

    const orderDate = new Date(createdAt)
    const now = new Date()
    const diffInMinutes = (now.getTime() - orderDate.getTime()) / (1000 * 60)

    return diffInMinutes > 60
  })

  printPage() {
    window.print()
  }

  cancelOrder(): void {
    // Navigate to the contact page with prefilled values.
    this.router.navigate(['/contact'], {
      state: {
        initialFormValues: {
          order_uuid: this.order()?.uuid,
          first_name: this.order()?.contact_info?.first_name,
          last_name: this.order()?.contact_info?.last_name,
          email: this.order()?.contact_info?.email || this.order()?.email,
          message: `Please cancel my order (Order ID: ${this.order()?.uuid}).`
        }
      }
    })
  }

  get showPrices(): boolean {
    return !this.disablePrices && !this.isKitOrder()
  }
}
